import { useTranslation } from 'react-i18next';
import { useEffect, useState } from "react";
import axios from "axios";
import { Nav, Tab, Modal } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import ca from 'date-fns/locale/ca';
import fr from 'date-fns/locale/fr';
import de from 'date-fns/locale/de';
import pt from 'date-fns/locale/pt';
import ru from 'date-fns/locale/ru';

const LOCALES = {
    es,
    ca,
    fr,
    de,
    pt,
    ru,
};

const DATE_LOCALES = {
    es: "es-ES",
    ca: "ca-ES",
    fr: "fr-FR",
    de: "de-DE",
    pt: "pt-PT",
    ru: "ru-RU",
};


const DatosReserva = ({ reserva, lang, paises, tiposdocs, idiomas, config }) => {

    const { t } = useTranslation();

    var preidioma = ""
    if (lang != 'es') preidioma = lang + "/";

    if (lang == "es")
        registerLocale('es', es)
    else if (lang == "fr")
        registerLocale('fr', fr)
    else if (lang == "ca")
        registerLocale('ca', ca)
    else if (lang == "de")
        registerLocale('de', de)
    else if (lang == "pt")
        registerLocale('pt', pt)
    else if (lang == "ru")
        registerLocale('ru', ru)
    else
        registerLocale('es', es)

    const cliente = reserva?.cliente || {};
    const accent = config.color_corporativo_web;

    /*
     * ---------------------------------------------------------
     * Convierte un valor a Date solo si es una fecha válida.
     * Evita el "Invalid time value" de react-datepicker cuando
     * el backend envía una fecha vacía o mal formada.
     * ---------------------------------------------------------
     */
    const parseValidDate = (value) => {
        if (!value) return null;
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    /*
     * ---------------------------------------------------------
     * ESTADO DEL FORMULARIO
     * ---------------------------------------------------------
     */
    const [formData, setFormData] = useState({
        idcliente: cliente.id,
        nombre: cliente.nombre || "",
        apellidos: cliente.apellidos || "",
        telefono: cliente.telefono || "",
        fecha_nacimiento: parseValidDate(cliente.fecha_nacimiento),
        nacionalidad: cliente.nacionalidad || "",
        pais_id: cliente.pais_id || "",
        tipo_documento_id: cliente.tipo_documento_id || "",
        documento_identidad: cliente.documento_identidad || "",
        direccion: cliente.direccion || "",
        provincia: cliente.provincia || "",
        ciudad: cliente.ciudad || "",
        codigo_postal: cliente.codigo_postal || "",
        email: cliente.email || "",
        idioma: cliente.idioma || "",
        ofertas: cliente.newsletter || false,
    });

    const [activeTab, setActiveTab] = useState("1");
    const [saving, setSaving] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [botonCancel, setBotonCancel] = useState(reserva.estado_id === 3 ? "Cancelar" : "Cancelada")
    const [message, setMessage] = useState({
        type: "",
        text: "",
    });

    const [showMessage, setShowMessage] = useState(false);

    useEffect(() => {
        if (message.text) {
            setShowMessage(true);

            const timer = setTimeout(() => {
                setShowMessage(false);
            }, 3000); // 5 segundos

            return () => clearTimeout(timer);
        }

        setShowMessage(false);
    }, [message.text]);

    /*
     * ---------------------------------------------------------
     * REGISTRAR LOCALE DEL DATEPICKER
     * ---------------------------------------------------------
     */
    useEffect(() => {
        const locale = LOCALES[lang] || es;
        const localeName = LOCALES[lang] ? lang : "es";

        registerLocale(localeName, locale);
    }, [lang]);

    /*
     * ---------------------------------------------------------
     * ACTUALIZAR FORMULARIO SI CAMBIA LA RESERVA
     * ---------------------------------------------------------
     */
    useEffect(() => {
        setFormData({
            idcliente: cliente.id,
            nombre: cliente.nombre || "",
            apellidos: cliente.apellidos || "",
            telefono: cliente.telefono || "",
            fecha_nacimiento: parseValidDate(cliente.fecha_nacimiento),
            nacionalidad: cliente.nacionalidad || "",
            pais_id: cliente.pais_id || "",
            tipo_documento_id: cliente.tipo_documento_id || "",
            documento_identidad: cliente.documento_identidad || "",
            direccion: cliente.direccion || "",
            provincia: cliente.provincia || "",
            ciudad: cliente.ciudad || "",
            codigo_postal: cliente.codigo_postal || "",
            email: cliente.email || "",
            idioma: cliente.idioma || "",
            ofertas: cliente.newsletter || false,
        });
    }, [reserva]);

    /*
    * ---------------------------------------------------------
    * HANDLE CHANGE
    * ---------------------------------------------------------
    */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    /*
     * ---------------------------------------------------------
     * TELÉFONO
     * ---------------------------------------------------------
     */
    const handlePhoneChange = (e) => {
        const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");

        setFormData((prev) => ({
            ...prev,
            telefono: onlyNumbers.slice(0, 30),
        }));
    };

    /*
   * ---------------------------------------------------------
   * FECHA DE NACIMIENTO
   * ---------------------------------------------------------
   */
    const handleBirthDateChange = (date) => {
        setFormData((prev) => ({
            ...prev,
            fecha_nacimiento: date,
        }));
    };

    /*
     * ---------------------------------------------------------
     * CANCELAR RESERVA
     * ---------------------------------------------------------
     */
    const abrirModalCancelar = () => {
        if (!reserva?.id) {
            setMessage({
                type: "danger",
                text: t("No se ha encontrado la reserva."),
            });

            return;
        }

        setShowCancelModal(true);
    };

    const cancelarReserva = async () => {
        setShowCancelModal(false);

        try {
            setCancelling(true);
            setBotonCancel("Cancelando...");
            setMessage({
                type: "",
                text: "",
            });

            const response = await axios.post("cancelareserva", {
                idreserva: reserva.id
            });

            console.log("Reserva cancelada:", response.data);

            if (response.data.estado == "ok") {
                setMessage({
                    type: "success",
                    text: t("La reserva se ha cancelado correctamente."),
                });
                setBotonCancel("Cancelada");
            }
            else {
                setMessage({
                    type: "danger",
                    text: t("No se ha podido cancelar la reserva."),
                });
                setBotonCancel("Cancelar");
            }

        } catch (error) {
            console.error("Error cancelando la reserva:", error);
            setBotonCancel("Cancelar");

            setMessage({
                type: "danger",
                text:
                    error?.response?.data?.message ||
                    t("No se ha podido cancelar la reserva."),
            });
        } finally {
            setCancelling(false);
        }
    };

    /*
     * ---------------------------------------------------------
     * GUARDAR DATOS DEL CLIENTE
     * ---------------------------------------------------------
     */
    const guardarCliente = async (e) => {
        e.preventDefault();

        if (!cliente?.id) {
            setMessage({
                type: "danger",
                text: t("No se ha encontrado el cliente."),
            });

            return;
        }

        try {
            setSaving(true);
            setMessage({
                type: "",
                text: "",
            });

            /*
             * Convertimos la fecha a YYYY-MM-DD.
             */
            let fechaNacimiento = null;

            if (formData.fecha_nacimiento instanceof Date && !isNaN(formData.fecha_nacimiento.getTime())) {
                const year = formData.fecha_nacimiento.getFullYear();
                const month = String(
                    formData.fecha_nacimiento.getMonth() + 1
                ).padStart(2, "0");
                const day = String(
                    formData.fecha_nacimiento.getDate()
                ).padStart(2, "0");

                fechaNacimiento = `${year}-${month}-${day}`;
            }

            const data = {
                idcliente: formData.idcliente,
                nombre: formData.nombre,
                apellidos: formData.apellidos,
                telefono: formData.telefono,
                fecha_nacimiento: fechaNacimiento,
                nacionalidad: formData.nacionalidad,
                pais_id: formData.pais_id,
                tipo_documento_id: formData.tipo_documento_id,
                documento_identidad: formData.documento_identidad,
                direccion: formData.direccion,
                provincia: formData.provincia,
                ciudad: formData.ciudad,
                codigo_postal: formData.codigo_postal,
                email: formData.email,
                idioma: formData.idioma,
                ofertas: formData.ofertas,
            };

            /*
             * Cambia esta URL por el endpoint real de tu API.
             */
            const response = await axios.post(
                "actualizacliente",
                data
            );

            console.log("Cliente actualizado:", response.data);

            setMessage({
                type: "success",
                text: t("Los datos se han guardado correctamente."),
            });

        } catch (error) {
            console.error("Error guardando los datos del cliente:", error);

            setMessage({
                type: "danger",
                text:
                    error?.response?.data?.message ||
                    t("No se han podido guardar los datos."),
            });
        } finally {
            setSaving(false);
        }
    };

    /*
     * ---------------------------------------------------------
     * FORMATO DE FECHAS
     * ---------------------------------------------------------
     */
    const formatDate = (date) => {
        if (!date) {
            return "";
        }

        return new Date(date).toLocaleDateString(
            DATE_LOCALES[lang] || "es-ES",
            {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }
        );
    };

    /*
     * ---------------------------------------------------------
     * NOCHES
     * ---------------------------------------------------------
     */
    const calcularNoches = () => {
        if (!reserva?.inicio || !reserva?.final) {
            return 0;
        }

        const inicio = new Date(reserva.inicio);
        const final = new Date(reserva.final);

        return Math.round(
            (final.getTime() - inicio.getTime()) /
            (1000 * 60 * 60 * 24)
        );
    };

    /*
     * ---------------------------------------------------------
     * PEQUEÑOS BLOQUES DE UI REUTILIZABLES
     * ---------------------------------------------------------
     */
    const navLinkStyle = (key) => ({
        color: activeTab === key ? "#fff" : "#495057",
        background: activeTab === key ? accent : "transparent",
    });

    const SectionLabel = ({ icon, children }) => (
        <div
            className="gap-2 pb-2 mb-3 d-flex align-items-center text-uppercase small border-bottom"
            style={{ color: accent, letterSpacing: "0.06em" }}
        >
            <i className={`fa-duotone ${icon}`} aria-hidden="true"></i>
            <span>{children}</span>
        </div>
    );

    const Stat = ({ icon, label, value, col = "col-6 col-md-2" }) => (
        <div className={col}>
            <div className="gap-2 mb-2 d-flex align-items-center text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>
                <i className={`fa-duotone ${icon}`} aria-hidden="true"></i>
                <span>{label}</span>
            </div>
            <div className="fw-light text-truncate" style={{ fontSize: "0.9rem" }}>{value || "—"}</div>
        </div>
    );

    return (
        <section className="py-5" style={{ background: "#f7f7f8", minHeight: "70vh" }}>
            <div className="container">
                <Tab.Container
                    id="gestion-reserva-tabs"
                    activeKey={activeTab}
                    onSelect={(key) => setActiveTab(key)}
                >
                    <div>
                        {/* =====================================================
                            MENÚ
                        ====================================================== */}
                        <Nav className="flex-row gap-2 p-2 mb-4 bg-white shadow-sm d-flex rounded-4">
                            <Nav.Item className="flex-fill flex-md-grow-0">
                                <Nav.Link eventKey="1" className="text-center rounded-3 fw-light small" style={navLinkStyle("1")}>
                                    <i className="fa-duotone fa-calendar-check me-2" aria-hidden="true"></i>
                                    {t("Datos de la reserva")}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="flex-fill flex-md-grow-0">
                                <Nav.Link eventKey="2" className="text-center rounded-3 fw-light small" style={navLinkStyle("2")}>
                                    <i className="fa-duotone fa-user me-2" aria-hidden="true"></i>
                                    {t("Datos del cliente")}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        {/* =====================================================
                            CONTENIDO
                        ====================================================== */}
                        <div>
                            {/* MENSAJES */}
                            {message.text && showMessage && (
                                <div
                                    className={`alert alert-${message.type} d-flex align-items-center gap-2`}
                                    role="alert"
                                >
                                    <i
                                        className={`fa-duotone ${message.type === "success" ? "fa-circle-check" : "fa-circle-exclamation"}`}
                                        aria-hidden="true"
                                    ></i>
                                    <span>{message.text}</span>
                                </div>
                            )}

                            <div className="p-4 bg-white shadow-sm rounded-4 p-md-5">
                                <Tab.Content>

                                    {/* =============================================
                                        DATOS DE LA RESERVA
                                    ============================================== */}
                                    <Tab.Pane eventKey="1">
                                        <SectionLabel icon="fa-calendar-check">
                                            {t("Datos de la reserva")}
                                        </SectionLabel>

                                        <div className="mt-2 mb-4 row g-4">
                                            <Stat icon="fa-plane-arrival" label={t("Entrada")} value={formatDate(reserva.inicio)} />
                                            <Stat icon="fa-plane-departure" label={t("Salida")} value={formatDate(reserva.final)} />
                                            <Stat icon="fa-house" label={t("Propiedad")} value={reserva?.propiedad?.nombre} col="col-12 col-md-4" />
                                            <Stat icon="fa-moon" label={t("Noches")} value={calcularNoches()} />

                                            <div className="col-6 col-md-2">
                                                <div className="gap-2 mb-2 d-flex align-items-center text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>
                                                    <i className="fa-duotone fa-circle-info" aria-hidden="true"></i>
                                                </div>

                                                {reserva.estado_id !== 3 ? (
                                                    <span
                                                        className="gap-2 btn btn-sm fw-light d-inline-flex align-items-center"
                                                        style={{
                                                            background: "transparent",
                                                            border: "1px solid #dc3545",
                                                            color: "#dc3545",
                                                            pointerEvents: "none",
                                                        }}
                                                    >
                                                        <i className="fa-duotone fa-ban" aria-hidden="true"></i>
                                                        {t(botonCancel)}
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="gap-2 btn btn-sm fw-light d-inline-flex align-items-center"
                                                        id="botoncancelar"
                                                        disabled={cancelling}
                                                        onClick={abrirModalCancelar}
                                                        style={{
                                                            background: "transparent",
                                                            border: `1px solid ${accent}`,
                                                            color: accent,
                                                        }}
                                                    >
                                                        <i className="fa-duotone fa-ban" aria-hidden="true"></i>
                                                        {t(botonCancel)}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </Tab.Pane>

                                    {/* =============================================
                                        DATOS CLIENTE
                                    ============================================== */}
                                    <Tab.Pane eventKey="2">
                                        <form noValidate onSubmit={guardarCliente}>

                                            {/* DATOS PERSONALES */}
                                            <SectionLabel icon="fa-user">{t("Datos personales")}</SectionLabel>
                                            <div className="mb-4 row g-3">
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="nombre" className="form-label small text-uppercase text-muted">
                                                        {t("Nombre")} <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        id="nombre"
                                                        name="nombre"
                                                        maxLength="30"
                                                        type="text"
                                                        className="form-control fw-light"
                                                        value={formData.nombre}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="primer_apellido" className="form-label small text-uppercase text-muted">
                                                        {t("Apellidos")} <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        id="primer_apellido"
                                                        name="apellidos"
                                                        maxLength="30"
                                                        type="text"
                                                        className="form-control fw-light"
                                                        value={formData.apellidos}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="telf" className="form-label small text-uppercase text-muted">
                                                        {t("Teléfono")} <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        maxLength="30"
                                                        id="telf"
                                                        name="telefono"
                                                        type="tel"
                                                        className="form-control fw-light"
                                                        value={formData.telefono}
                                                        onChange={handlePhoneChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="fecha" className="form-label small text-uppercase text-muted">
                                                        {t("Fecha de nacimiento")} <span className="text-danger">*</span>
                                                    </label>
                                                    <DatePicker
                                                        isClearable
                                                        selected={formData.fecha_nacimiento}
                                                        onChange={handleBirthDateChange}
                                                        monthsShown={1}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText={t("DD/MM/YYYY")}
                                                        locale={LOCALES[lang] ? lang : "es"}
                                                        name="fecha"
                                                        id="fecha"
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        className="form-control fw-light"
                                                        autoComplete="off"
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="nacionalidad" className="form-label small text-uppercase text-muted">
                                                        {t("Nacionalidad")} <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        id="nacionalidad"
                                                        name="nacionalidad"
                                                        className="form-select fw-light"
                                                        value={formData.nacionalidad}
                                                        onChange={handleChange}
                                                        required
                                                    >
                                                        <option value="">{t("Seleccione")}</option>
                                                        {paises.map((unpais) => (
                                                            <option value={unpais.id} key={unpais.id}>
                                                                {unpais.nombre}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="pais_id" className="form-label small text-uppercase text-muted">
                                                        {t("País de residencia")} <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        id="pais_id"
                                                        name="pais_id"
                                                        className="form-select fw-light"
                                                        value={formData.pais_id}
                                                        onChange={handleChange}
                                                        required
                                                    >
                                                        <option value="">{t("Seleccione")}</option>
                                                        {paises.map((unpais) => (
                                                            <option value={unpais.id} key={unpais.id}>
                                                                {unpais.nombre}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* DOCUMENTO DE IDENTIDAD */}
                                            <SectionLabel icon="fa-id-card">{t("Documento de identidad")}</SectionLabel>
                                            <div className="mb-4 row g-3">
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="doctype" className="form-label small text-uppercase text-muted">
                                                        {t("Tipo de documento")}
                                                    </label>
                                                    <select
                                                        id="doctype"
                                                        name="tipo_documento_id"
                                                        className="form-select fw-light"
                                                        disabled
                                                        value={formData.tipo_documento_id}
                                                        onChange={handleChange}
                                                    >
                                                        {tiposdocs.map((untipodoc) => (
                                                            <option value={untipodoc.id} key={untipodoc.id}>
                                                                {untipodoc.nombre}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="documento" className="form-label small text-uppercase text-muted">
                                                        {t("Número de documento")}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="documento"
                                                        maxLength="16"
                                                        className="form-control fw-light"
                                                        disabled
                                                        value={formData.documento_identidad}
                                                        readOnly
                                                    />
                                                </div>
                                            </div>

                                            {/* DIRECCIÓN */}
                                            <SectionLabel icon="fa-location-dot">{t("Dirección")}</SectionLabel>
                                            <div className="mb-4 row g-3">
                                                <div className="col-sm-6 col-md-4">
                                                    <label className="form-label small text-uppercase text-muted" htmlFor="direccion">
                                                        {t("Dirección")}
                                                    </label>
                                                    <input
                                                        maxLength="60"
                                                        type="text"
                                                        id="direccion"
                                                        name="direccion"
                                                        className="form-control fw-light"
                                                        value={formData.direccion}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="provincia" className="form-label small text-uppercase text-muted">
                                                        {t("Provincia")}
                                                    </label>
                                                    <input
                                                        maxLength="60"
                                                        type="text"
                                                        id="provincia"
                                                        name="provincia"
                                                        className="form-control fw-light"
                                                        value={formData.provincia}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="localidad" className="form-label small text-uppercase text-muted">
                                                        {t("Localidad")}
                                                    </label>
                                                    <input
                                                        maxLength="40"
                                                        type="text"
                                                        id="localidad"
                                                        name="ciudad"
                                                        className="form-control fw-light"
                                                        value={formData.ciudad}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="codigo_postal" className="form-label small text-uppercase text-muted">
                                                        {t("Código postal")}
                                                    </label>
                                                    <input
                                                        maxLength="10"
                                                        type="text"
                                                        id="codigo_postal"
                                                        name="codigo_postal"
                                                        className="form-control fw-light"
                                                        value={formData.codigo_postal}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>

                                            {/* CONTACTO Y COMUNICACIONES */}
                                            <SectionLabel icon="fa-envelope">{t("Contacto y comunicaciones")}</SectionLabel>
                                            <div className="mb-4 row g-3">
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="email_comunicaciones" className="form-label small text-uppercase text-muted">
                                                        {t("E-mail")}
                                                    </label>
                                                    <input
                                                        type="email"
                                                        id="email_comunicaciones"
                                                        name="email"
                                                        className="form-control fw-light"
                                                        disabled
                                                        value={formData.email}
                                                        readOnly
                                                    />
                                                </div>
                                                <div className="col-sm-6 col-md-4">
                                                    <label htmlFor="communicationLanguage" className="form-label small text-uppercase text-muted">
                                                        {t("Idioma de las comunicaciones")}
                                                    </label>
                                                    <select
                                                        id="communicationLanguage"
                                                        name="idioma"
                                                        className="form-select fw-light"
                                                        value={formData.idioma}
                                                        onChange={handleChange}
                                                    >
                                                        {idiomas.map((unidioma) => (
                                                            <option value={unidioma.id} key={unidioma.id}>
                                                                {unidioma.nombre}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="p-3 mb-4 rounded-3" style={{ background: "#f7f7f8" }}>
                                                <div className="gap-2 form-check d-flex align-items-start">
                                                    <input
                                                        type="checkbox"
                                                        id="ofertas"
                                                        name="ofertas"
                                                        className="mt-1 form-check-input"
                                                        checked={formData.ofertas}
                                                        onChange={handleChange}
                                                    />
                                                    <label htmlFor="ofertas" className="form-check-label fw-light">
                                                        <i className="fa-duotone fa-bell me-2" style={{ color: accent }} aria-hidden="true"></i>
                                                        {t("Acepto recibir comunicaciones comerciales")}
                                                        <span className="mt-1 d-block text-muted small">
                                                            {t("Para poder recibir promociones exclusivas, novedades y no perderte nada, permítenos contactarte.")}
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* GUARDAR */}
                                            <div className="d-flex justify-content-end">
                                                <button
                                                    type="submit"
                                                    className="text-white btn btn-sm fw-light"
                                                    disabled={saving}
                                                    style={{ background: accent, borderColor: accent }}
                                                >
                                                    {saving ? t("Guardando...") : t("Guardar cambios")}
                                                </button>
                                            </div>
                                        </form>
                                    </Tab.Pane>
                                </Tab.Content>
                            </div>
                        </div>
                    </div>
                </Tab.Container>

                <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                    <Modal.Header closeButton className="pb-0 border-0">
                        <Modal.Title className="fw-light" style={{ fontSize: "1rem" }}>
                            {t("Cancelar reserva")}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="fw-light">
                        {t("¿Estás seguro de que quieres cancelar esta reserva?")}
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <button
                            type="button"
                            className="btn btn-sm fw-light"
                            onClick={() => setShowCancelModal(false)}
                        >
                            {t("No")}
                        </button>
                        <button
                            type="button"
                            className="text-white btn btn-sm fw-light"
                            disabled={cancelling}
                            onClick={cancelarReserva}
                            style={{ background: accent, borderColor: accent }}
                        >
                            {t("Sí, cancelar")}
                        </button>
                    </Modal.Footer>
                </Modal>
            </div>
        </section>
    );

}
export default DatosReserva;
